/**
 * PublicPageShell — UNAUTH page chrome. Phase 3 §2 retire of the
 * legacy auth-aware branches.
 *
 * Always renders the v3 `<SharedNav>` (default Log in cluster) and
 * `<SharedFooter>`. Auth state is no longer read — authed users
 * never reach this shell except via `<PublicPageRouter>`, which
 * suppresses this inner chrome via the RoleShellActiveContext so
 * we don't get a double-nav.
 *
 * Used by Login, Signup, AcceptAdminInvite, Terms, Privacy, Contact,
 * About, Pricing, Map, Social, and the ComingSoon placeholder.
 *
 * Pass `mainClass` to control the `<main>` layout (default empty —
 * the caller's children handle their own internal layout). Examples:
 *   - Auth cards: "flex items-center justify-center px-6 py-12"
 *   - Long-form content pages: "" (the page renders its own sections)
 */
import React from "react";
import SharedNav from "@/components/SharedNav";
import SharedFooter from "@/components/SharedFooter";
import ContestPromoTopBar from "@/components/ContestPromoTopBar";
import { useRoleShellActive } from "@/components/PublicPageRouter";

export default function PublicPageShell({ children, mainClass = "" }) {
  // When an authed user lands on a public route, PublicPageRouter
  // wraps us with this flag so the role shell's chrome (already
  // mounted outside) is the only nav/footer the user sees.
  const inRoleShell = useRoleShellActive();
  if (inRoleShell) {
    return (
      <div className={mainClass || undefined} data-testid="public-page-main">
        {children}
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: "var(--pp-v3-bg)",
        // Item 11 (Feb 2026 launch-blocking batch) — impersonation
        // banner overlap fix. AdminModeBar publishes its live
        // `offsetHeight` as `--admin-mode-bar-h` on the document
        // root when impersonation is active. Every layout shell
        // (AdminShell / CustomerShell / ProviderShell) already
        // consumes this var; PublicPageShell was the missing surface
        // — an impersonated admin landing on any auth/legal/coming-
        // soon route saw SharedNav occluded by the red banner. The
        // `0px` fallback keeps this a zero-cost no-op when the var
        // is unset (banner not rendered).
        paddingTop: "var(--admin-mode-bar-h, 0px)",
      }}
      data-testid="public-page-shell"
    >
      {/* Contest Promo top-bar (2026-07-21 dispatch). Sits in-flow
          above SharedNav so it's naturally pushed below the fixed
          AdminModeBar by the paddingTop above. Self-suppresses when
          no contest round is active OR the user dismissed it this
          session (in-memory only — see ActiveContestContext). */}
      <ContestPromoTopBar />
      <SharedNav />
      <main className={`flex-1 ${mainClass}`} data-testid="public-page-main">
        {children}
      </main>
      <SharedFooter />
    </div>
  );
}
